import { Boxes } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import type { CameraPose } from '../../engine/camera-playback';
import type { Hotspot3DLabel } from '../../schema';

/**
 * Shared `<model-viewer>` wrapper: lazy-loads the custom element, applies a
 * controlled camera pose (with easing), renders 3D-anchored hotspot labels,
 * captures the current pose, and resolves a surface point under the cursor.
 * Ported from the admin app; placeholder states use inline styles so the
 * package needs no host theme.
 */

let loadPromise: Promise<void> | null = null;
export function loadModelViewer(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = import('@google/model-viewer')
    .then(() => undefined)
    .catch((err) => {
      loadPromise = null;
      throw err;
    });
  return loadPromise;
}

/** A point on the model surface, as model-viewer `data-position` / `data-normal` strings. */
export interface SurfaceHit {
  position: string;
  normal: string;
}

export interface ModelViewerHandle {
  capturePose: () => CameraPose | null;
  surfaceHitFromClient: (clientX: number, clientY: number) => SurfaceHit | null;
  recenter: () => void;
  getElement: () => HTMLElement | null;
}

interface Props {
  src: string;
  poster?: string;
  alt?: string;
  exposure?: number;
  autoRotate?: boolean;
  cameraControls?: boolean;
  /** Controlled camera pose. When set, the viewer eases to it (playback/preview). */
  pose?: CameraPose | null;
  /** model-viewer `interpolation-decay` — lower = snappier easing. Default 100. */
  interpolationDecay?: number;
  /** 3D labels to render as hotspots (track the model as the camera moves). */
  labels?: Hotspot3DLabel[];
  className?: string;
  style?: React.CSSProperties;
  onReady?: () => void;
  onError?: (msg: string) => void;
  onModelClick?: (hit: SurfaceHit) => void;
}

const PLACEHOLDER_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
};

export const ModelViewerCanvas = forwardRef<ModelViewerHandle, Props>(function ModelViewerCanvas(
  {
    src,
    poster,
    alt,
    exposure,
    autoRotate,
    cameraControls,
    pose,
    interpolationDecay = 100,
    labels,
    className,
    style,
    onReady,
    onError,
    onModelClick,
  },
  ref,
) {
  const elRef = useRef<HTMLElement | null>(null);
  const lastPoseRef = useRef<string>('');
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useImperativeHandle(
    ref,
    (): ModelViewerHandle => ({
      capturePose: () => {
        const el = elRef.current as unknown as {
          getCameraOrbit?: () => { toString: () => string };
          getCameraTarget?: () => { toString: () => string };
          getFieldOfView?: () => number;
        } | null;
        if (!el || typeof el.getCameraOrbit !== 'function') return null;
        const orbit = el.getCameraOrbit?.();
        const target = el.getCameraTarget?.();
        const fov = el.getFieldOfView?.();
        if (!orbit) return null;
        return {
          cameraOrbit: orbit.toString(),
          cameraTarget: target ? target.toString() : undefined,
          fieldOfView: typeof fov === 'number' ? `${fov}deg` : undefined,
        };
      },
      surfaceHitFromClient: (clientX, clientY) => {
        const el = elRef.current as unknown as {
          positionAndNormalFromPoint?: (
            x: number,
            y: number,
          ) => { position: { toString: () => string }; normal: { toString: () => string } } | null;
        } | null;
        if (!el || typeof el.positionAndNormalFromPoint !== 'function') return null;
        const hit = el.positionAndNormalFromPoint?.(clientX, clientY);
        if (!hit) return null;
        return { position: hit.position.toString(), normal: hit.normal.toString() };
      },
      recenter: () => {
        const el = elRef.current;
        if (!el) return;
        el.setAttribute('camera-target', 'auto auto auto');
        el.setAttribute('camera-orbit', '0deg 75deg 105%');
        lastPoseRef.current = '';
      },
      getElement: () => elRef.current,
    }),
    [],
  );

  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    loadModelViewer()
      .then(() => {
        if (cancelled) return;
        setReady(true);
        onReady?.();
      })
      .catch(() => {
        if (cancelled) return;
        setError('Could not load 3D viewer.');
        onError?.('Could not load 3D viewer.');
      });
    return () => {
      cancelled = true;
    };
  }, [src, onReady, onError]);

  // Apply the controlled pose imperatively (deduped by value). Sets the damper
  // decay (move speed) right before the orbit so the move eases at that speed.
  useEffect(() => {
    const el = elRef.current;
    if (!el || !ready || !pose) return;
    const key = `${pose.cameraOrbit}|${pose.cameraTarget ?? ''}|${pose.fieldOfView ?? ''}|${interpolationDecay}`;
    if (key === lastPoseRef.current) return;
    lastPoseRef.current = key;
    el.setAttribute('interpolation-decay', String(interpolationDecay));
    if (pose.cameraOrbit) el.setAttribute('camera-orbit', pose.cameraOrbit);
    if (pose.cameraTarget) el.setAttribute('camera-target', pose.cameraTarget);
    if (pose.fieldOfView) el.setAttribute('field-of-view', pose.fieldOfView);
  }, [pose, ready, interpolationDecay]);

  // Surface picking via a NATIVE capture-phase click listener (React's onClick
  // on the custom element can be swallowed). Pass RAW client coords — model-
  // viewer's getNDC() subtracts the element rect itself.
  useEffect(() => {
    const el = elRef.current as unknown as
      | (HTMLElement & {
          positionAndNormalFromPoint?: (
            x: number,
            y: number,
          ) =>
            | { position: { toString: () => string }; normal: { toString: () => string } }
            | null;
        })
      | null;
    if (!el || !ready || !onModelClick) return;
    const handler = (e: MouseEvent) => {
      if (typeof el.positionAndNormalFromPoint !== 'function') return;
      const hit = el.positionAndNormalFromPoint(e.clientX, e.clientY);
      if (hit) onModelClick({ position: hit.position.toString(), normal: hit.normal.toString() });
    };
    el.addEventListener('click', handler, true);
    return () => el.removeEventListener('click', handler, true);
  }, [ready, onModelClick]);

  if (!src) {
    return (
      <div
        className={className}
        style={{
          ...PLACEHOLDER_STYLE,
          background: 'linear-gradient(135deg, #e0e7ff, #f3e8ff)',
          color: '#4338ca',
          ...style,
        }}
      >
        <Boxes size={24} />
        <span style={{ fontSize: 12, fontWeight: 500 }}>No model</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={className}
        style={{ ...PLACEHOLDER_STYLE, background: '#fef2f2', color: '#dc2626', padding: 12, ...style }}
      >
        <Boxes size={20} />
        {error}
      </div>
    );
  }

  if (!ready) {
    return (
      <div
        className={className}
        style={{ ...PLACEHOLDER_STYLE, background: '#f1f5f9', color: '#64748b', ...style }}
      >
        <Boxes size={20} className="animate-pulse" />
        <span style={{ fontSize: 11, fontWeight: 500 }}>Loading 3D viewer…</span>
      </div>
    );
  }

  return (
    // @ts-expect-error -- model-viewer is a custom element registered at runtime
    <model-viewer
      ref={elRef}
      src={src}
      poster={poster || undefined}
      alt={alt || '3D model'}
      camera-controls={cameraControls ? '' : undefined}
      auto-rotate={autoRotate ? '' : undefined}
      exposure={exposure ?? 1}
      shadow-intensity="1"
      interaction-prompt="none"
      class={className}
      // --min-hotspot-opacity: 0 → labels fully hide when their anchor faces away.
      style={{ ['--min-hotspot-opacity' as string]: 0, ...style } as React.CSSProperties}
    >
      <HotspotLabels labels={labels ?? []} />
      {/* @ts-expect-error -- closing custom element tag */}
    </model-viewer>
  );
});

const LABEL_OX = 58;
const LABEL_OY = 46;

/**
 * Renders the 3D labels as model-viewer hotspots — a contact dot, dashed
 * connector, and offset chip. Keeps a label mounted through its exit animation
 * so labels animate both in and out as keyframes activate/deactivate.
 */
function HotspotLabels({ labels }: { labels: Hotspot3DLabel[] }) {
  const [rendered, setRendered] = useState<Array<{ label: Hotspot3DLabel; leaving: boolean }>>(() =>
    labels.map((label) => ({ label, leaving: false })),
  );
  const timers = useRef<Record<string, number>>({});

  useEffect(() => {
    const incoming = new Map(labels.map((l) => [l.id, l]));
    setRendered((prev) => {
      const next: Array<{ label: Hotspot3DLabel; leaving: boolean }> = [];
      for (const l of labels) {
        if (timers.current[l.id]) {
          window.clearTimeout(timers.current[l.id]);
          delete timers.current[l.id];
        }
        next.push({ label: l, leaving: false });
      }
      for (const r of prev) {
        if (!incoming.has(r.label.id)) next.push({ label: r.label, leaving: true });
      }
      return next;
    });
  }, [labels]);

  useEffect(() => {
    for (const r of rendered) {
      if (r.leaving && !timers.current[r.label.id]) {
        timers.current[r.label.id] = window.setTimeout(() => {
          delete timers.current[r.label.id];
          setRendered((prev) => prev.filter((p) => p.label.id !== r.label.id));
        }, 420);
      }
    }
  }, [rendered]);

  return (
    <>
      {rendered.map(({ label, leaving }) => {
        const anim = label.animation ?? 'fade';
        const phase = leaving ? `kf-exit-${anim}` : `kf-enter-${anim}`;
        const color = label.color;
        const left = label.side === 'left';
        return (
          <button
            key={label.id}
            slot={`hotspot-${label.id}`}
            data-position={label.position}
            data-normal={label.normal}
            className="kf-hotspot"
          >
            <span className={`kf-hotspot-inner ${phase}`}>
              <span className="kf-hotspot-dot" style={color ? { background: color } : undefined} />
              <svg
                className="kf-hotspot-connector"
                width={LABEL_OX}
                height={LABEL_OY}
                viewBox={`0 0 ${LABEL_OX} ${LABEL_OY}`}
                style={left ? { right: 0 } : { left: 0 }}
                aria-hidden="true"
              >
                <line
                  x1={left ? LABEL_OX - 1 : 1}
                  y1={LABEL_OY - 1}
                  x2={left ? 2 : LABEL_OX - 2}
                  y2="2"
                  stroke={color ?? '#0f172a'}
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                />
              </svg>
              <span
                className="kf-hotspot-label"
                style={{
                  ...(left ? { right: LABEL_OX } : { left: LABEL_OX }),
                  bottom: LABEL_OY,
                  fontSize: label.fontSize ?? 13,
                  textAlign: left ? 'right' : 'left',
                }}
              >
                {label.text}
              </span>
            </span>
          </button>
        );
      })}
    </>
  );
}
